import { ImpersonationGrantSummary } from './types'

// E11 R5 (WI 612) — client-side CSV export of the current (filtered) grants view. buildGrantsCsv is pure so
// it is unit-testable; downloadCsv is the DOM side-effect. Values are RFC-4180 quoted (double-quotes doubled;
// always quoted so embedded commas/newlines in a reason are safe).

const HEADERS = [
  'Status',
  'Grantor',
  'Grantee',
  'Target',
  'Target email',
  'Target tenant',
  'Granted at',
  'Expires at',
  'Revoked at',
  'Reason',
] as const

const quote = (value: string | null | undefined): string => `"${(value ?? '').replace(/"/g, '""')}"`

// Prefer the display name; fall back to "User #{id}" when the server-composed name is blank.
const nameOrId = (name: string | undefined, id: number): string =>
  name && name.trim() ? name : `User #${id}`

// A UTF-8 byte-order mark. Prepended to the download so Excel on Windows — which ignores the charset MIME
// hint and defaults to the system locale — reads the file as UTF-8 rather than garbling non-ASCII names.
const UTF8_BOM = '﻿'

export const buildGrantsCsv = (grants: ImpersonationGrantSummary[]): string => {
  const rows = grants.map((g) =>
    [
      g.status,
      nameOrId(g.grantorDisplayName, g.grantedByUserId),
      nameOrId(g.granteeDisplayName, g.granteeUserId),
      nameOrId(g.targetDisplayName, g.targetUserId),
      g.targetEmail,
      g.targetTenantName,
      g.startsAt,
      g.expiresAt,
      g.revokedAt ?? '',
      g.reason,
    ]
      .map(quote)
      .join(','),
  )
  return [HEADERS.map(quote).join(','), ...rows].join('\r\n')
}

// Trigger a browser download of the CSV text. Extracted so the page (and tests) can build the CSV without a DOM.
export const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([UTF8_BOM, content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
